import {
  BankAccountInfiniteSelectField,
  FloatingFormItem,
  Group,
} from '@/components/_common';
import { UploadArchiveResult } from '@/core/domain/archive/Archive/Archive.types';
import { ReadTransactionsFileFormValues } from '@/core/domain/financial/BankConciliation/BankConciliation.types';
import { useDefaultEmptyFieldError, useWindowSize } from '@/hooks';
import {
  Col,
  Form,
  Modal,
  ModalProps,
  Row,
  Tooltip,
  Upload,
  UploadFile,
} from 'antd';
import { Rule } from 'antd/lib/form';
import { useState } from 'react';
import { Inbox, Info } from 'react-feather';

interface Props extends Omit<ModalProps, 'onCancel'> {
  open: boolean;
  onClose: VoidFunction;
  onSubmit: (values?: ReadTransactionsFileFormValues) => Promise<void>;
}

export default function ReadTransactionsFileModal(props: Props) {
  const fieldRequired = useDefaultEmptyFieldError();
  const { isMobile } = useWindowSize();

  const [form] = Form.useForm<ReadTransactionsFileFormValues>();

  const [isLoading, setIsLoading] = useState(false);

  function normalizeFiles(e: { file: UploadFile<UploadArchiveResult> }) {
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.file;
  }

  function handleClose() {
    form.resetFields();
    props.onClose();
  }

  const uploadValidator: Rule = {
    validator(_, file: UploadFile<UploadArchiveResult>) {
      return new Promise((res, rej) => {
        if (file.status === 'error') {
          rej(file.error);
        }

        res(null);
      });
    },
  };

  async function handleSubmit() {
    try {
      const values = await form.validateFields();

      setIsLoading(true);

      await props.onSubmit({
        file: values.file,
        bankAccountId: values.bankAccountId,
      });

      handleClose();
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Modal
      open={props.open}
      title="Vincular anexos a conta"
      width={500}
      okText="Enviar anexo"
      onOk={handleSubmit}
      confirmLoading={isLoading}
      onCancel={handleClose}
    >
      <Form form={form}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Group gap={10} align="center">
              <b>Dados do arquivo bancário</b>
              <Tooltip
                placement="right"
                trigger={isMobile ? 'click' : 'hover'}
                title="A conta bancária selecionada deve ser a mesma associada ao arquivo OFX que será enviado."
              >
                <Info size={20} cursor="pointer" />
              </Tooltip>
            </Group>
          </Col>

          <Col span={24}>
            <FloatingFormItem
              name="bankAccountId"
              label="Conta bancária"
              rules={[fieldRequired]}
            >
              <BankAccountInfiniteSelectField
                allowClear
                queryFilter={{ active: true }}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <FloatingFormItem
              name="file"
              getValueFromEvent={normalizeFiles}
              rules={[fieldRequired, uploadValidator]}
              initialValue={[]}
              valuePropName="file"
            >
              <Upload
                disabled={isLoading}
                type="drag"
                listType="text"
                maxCount={1}
                accept=""
                customRequest={({ onSuccess }) => {
                  if (onSuccess) {
                    onSuccess(null);
                  }
                }}
              >
                <p className="ant-upload-drag-icon">
                  <Inbox size={32} />
                </p>
                <p className="ant-upload-text">Clique ou arraste os arquivos</p>
                <p className="ant-upload-hint">
                  Upload máximo de 1 arquivo, são permitidos: OFX
                </p>
              </Upload>
            </FloatingFormItem>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
